import { EventEmitter, Output, ViewChild } from '@angular/core';
import { Component, Input, OnInit } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { TranslationService } from 'src/app/shared';
import { FormV2ElementTypeEnum } from '../shared/dto/form-v2-element-type.enum';
import { FormV2ElementDto } from '../shared/dto/form-v2-element.dto';
import { FormV2ElementMetadataDto } from '../shared/dto/form-v2-element-metadata.dto';
import { ProfileMetadataLego } from '../shared/dto/profile-metadata.lego';
import { NgForm } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import { PurchaseItemMetadataLego } from '../shared/dto/purchase-item-metadata.lego';
import { FormV2TypeEnum } from '../shared/enum/form-v2-type.enum';
import { AffiliationCodeMetadataLego } from '../shared/dto/affiliation-code-metadata.lego';
import { CustomFieldMetadataLego } from '../shared/dto/custom-field-metadata.lego';
import { ListingMetadataLego } from '../shared/dto/listing-metadata.lego';
import { MembershipLevelMetadataLego } from '../shared/dto/membership-level-metadata.lego';
import { SocialMediaMetadataLego } from '../shared/dto/social-media-metadata.lego';
import { FileUploadMetadataLego } from '../shared/dto/file-upload-metadata-lego';
import { OpenFieldMetadataLego } from '../shared/dto/open-field-metadata.lego';
import { OpenFieldTypeEnum } from '../shared/dto/open-field-type.enum';

@Component({
  selector: 'wl-form-v2-element-edit',
  templateUrl: './form-v2-element-edit.component.html',
  styleUrls: ['./form-v2-element-edit.component.less'],
    standalone: false
})
export class FormV2ElementEditComponent implements OnInit {
  proposedElement: FormV2ElementDto;
  elementType = FormV2ElementTypeEnum;
  elementTypeOptions: Array<SelectItem>;
  displayHtmlEditor: boolean = false;
  @Input() show = false;
  @Input() formType: FormV2TypeEnum;

  @Output() saveClicked = new EventEmitter<FormV2ElementDto>();
  @Output() cancelClicked = new EventEmitter<void>();
  @ViewChild(NgForm) private editForm: NgForm;

  constructor(private _translationService: TranslationService) {
    this.proposedElement = { DisplayValue: '' } as FormV2ElementDto;
  }

  ngOnInit() {
    this.loadElementTypes();
  }

  open(formElement: FormV2ElementDto) {
    this.show = true;
    this.proposedElement = cloneDeep(formElement);
    if (formElement.ElementType == FormV2ElementTypeEnum.TextBlock) {
      this.displayHtmlEditor = true;
    }
  }

  private async loadElementTypes() {
    let headerLabel = await this._translationService.get('form-builder.element-type.header');
    let textBlockLabel = await this._translationService.get('form-builder.element-type.text-block');
    let profileIndLabel = await this._translationService.get('form-builder.element-type.profile-individual');
    let profileOrgLabel = await this._translationService.get('form-builder.element-type.profile-organization');
    let purchaseItemLabel = await this._translationService.get('form-builder.element-type.purchase-item');
    let customFieldsLabel = await this._translationService.get('form-builder.element-type.custom-field');
    let affiliationCodesLabel = await this._translationService.get('form-builder.element-type.affiliation-code');
    let listingsLabel = await this._translationService.get('form-builder.element-type.listing');
    let membershipLevelLabel = await this._translationService.get('form-builder.element-type.membership-level.title');
    let socialMediaLabel = await this._translationService.get('form-builder.element-type.social-media');
    let fileUploadLabel = await this._translationService.get('form-builder.element-type.file-upload');
    let openFieldLabel = await this._translationService.get('form-builder.element-type.open-field');

    let includeProfileIndividual = [
      FormV2TypeEnum.Donation,
      FormV2TypeEnum.GeneralSurvey,
      FormV2TypeEnum.Renewal,
      FormV2TypeEnum.Prospect,
    ].some((x) => x == this.formType);
    let includeProfileOrganization = [
      FormV2TypeEnum.Donation,
      FormV2TypeEnum.GeneralSurvey,
      FormV2TypeEnum.Renewal,
      FormV2TypeEnum.Prospect,
    ].some((x) => x == this.formType);
    let includePurchaseItems = [FormV2TypeEnum.Donation, FormV2TypeEnum.GeneralSurvey, FormV2TypeEnum.Renewal].some(
      (x) => x == this.formType
    );
    let includeCustomFields = [FormV2TypeEnum.Donation, FormV2TypeEnum.GeneralSurvey, FormV2TypeEnum.Renewal, FormV2TypeEnum.Prospect].some(
      (x) => x == this.formType
    );
    let includeAffiliationCodes = [
      FormV2TypeEnum.Donation,
      FormV2TypeEnum.GeneralSurvey,
      FormV2TypeEnum.Renewal,
      FormV2TypeEnum.Prospect,
    ].some((x) => x == this.formType);
    let includeListings = [FormV2TypeEnum.Renewal, FormV2TypeEnum.GeneralSurvey, FormV2TypeEnum.Prospect].some((x) => x == this.formType);
    let includeMembershipLevel = [FormV2TypeEnum.Renewal].some((x) => x == this.formType);
    let includeSocialMedia = [FormV2TypeEnum.Prospect].some((x) => x == this.formType);

    this.elementTypeOptions = [
      { value: FormV2ElementTypeEnum.Header, label: headerLabel } as SelectItem,
      { value: FormV2ElementTypeEnum.TextBlock, label: textBlockLabel } as SelectItem,
      ...(includeProfileIndividual ? [{ value: FormV2ElementTypeEnum.ProfileIndividual, label: profileIndLabel } as SelectItem] : []),
      ...(includeProfileOrganization ? [{ value: FormV2ElementTypeEnum.ProfileOrganization, label: profileOrgLabel } as SelectItem] : []),
      ...(includePurchaseItems ? [{ value: FormV2ElementTypeEnum.PurchaseItem, label: purchaseItemLabel } as SelectItem] : []),
      ...(includeCustomFields ? [{ value: FormV2ElementTypeEnum.CustomField, label: customFieldsLabel } as SelectItem] : []),
      ...(includeAffiliationCodes ? [{ value: FormV2ElementTypeEnum.AffiliationCode, label: affiliationCodesLabel } as SelectItem] : []),
      ...(includeListings ? [{ value: FormV2ElementTypeEnum.Listings, label: listingsLabel } as SelectItem] : []),
      ...(includeMembershipLevel ? [{ value: FormV2ElementTypeEnum.MembershipLevel, label: membershipLevelLabel } as SelectItem] : []),
      ...(includeSocialMedia ? [{ value: FormV2ElementTypeEnum.SocialMedia, label: socialMediaLabel } as SelectItem] : []),
      { value: FormV2ElementTypeEnum.FileUpload, label: fileUploadLabel } as SelectItem,
      { value: FormV2ElementTypeEnum.OpenField, label: openFieldLabel } as SelectItem,
    ];
  }

  save() {
    this.saveClicked.emit(this.proposedElement);
    this.show = false;
    this.displayHtmlEditor = false;
  }

  cancel() {
    this.editForm.resetForm();
    this.cancelClicked.emit();
    this.show = false;
    this.displayHtmlEditor = false;
  }

  elementTypeChanged() {
    this.clearLegos();
    switch (this.proposedElement.ElementType) {
      case FormV2ElementTypeEnum.ProfileIndividual:
      case FormV2ElementTypeEnum.ProfileOrganization:
        this.proposedElement.MetaData.ProfileData = {} as ProfileMetadataLego;
        break;
      case FormV2ElementTypeEnum.PurchaseItem:
        this.proposedElement.MetaData.PurchaseItemData = {} as PurchaseItemMetadataLego;
        break;
      case FormV2ElementTypeEnum.CustomField:
        this.proposedElement.MetaData.CustomFieldData = {} as CustomFieldMetadataLego;
        break;
      case FormV2ElementTypeEnum.AffiliationCode:
        this.proposedElement.MetaData.AffiliationCodeData = {} as AffiliationCodeMetadataLego;
        break;
      case FormV2ElementTypeEnum.Listings:
        this.proposedElement.MetaData.ListingData = {} as ListingMetadataLego;
        break;
      case FormV2ElementTypeEnum.MembershipLevel:
        this.proposedElement.MetaData.MembershipLevelData = { MembershipLevelItems: [] } as MembershipLevelMetadataLego;
        break;
      case FormV2ElementTypeEnum.SocialMedia:
        this.proposedElement.MetaData.SocialMediaData = {} as SocialMediaMetadataLego;
        break;
      case FormV2ElementTypeEnum.FileUpload:
        this.proposedElement.MetaData.FileUploadData = {} as FileUploadMetadataLego;
        break;
      case FormV2ElementTypeEnum.OpenField:
        this.proposedElement.MetaData.OpenFieldData = { OpenFieldType: OpenFieldTypeEnum.Textbox } as OpenFieldMetadataLego;
        break;
      case FormV2ElementTypeEnum.TextBlock:
        this.proposedElement.DisplayValue = '';
        this.displayHtmlEditor = true;
        break;
    }
  }

  private clearLegos() {
    this.proposedElement.MetaData = {} as FormV2ElementMetadataDto;
  }
}
